import { isDefined, isNil } from "@clipboard-health/util-ts";
import {
  ShiftRateNegotiationRole,
  ShiftRateNegotiationStatus,
  WorkplaceShiftRateNegotiability,
} from "@src/appV2/OpenShifts/RateNegotiation/types";
import { BookabilityDecision, BookabilityUnmetCriteria } from "@src/appV2/OpenShifts/ShiftAction";
import { hasUnmetBookabilityCriteria } from "@src/appV2/OpenShifts/ShiftAction/hasUnmetBookabilityCriteria";
import { type BookabilityStatusItem } from "@src/appV2/OpenShifts/ShiftAction/types";

import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { type BlockingRequirementsContext } from "../Bookability/Documents/types";
import {
  type BackgroundCheckContext,
  getBackgroundCheckContext,
} from "../getBackgroundCheckContext";
import { isShiftRateNegotiationEnabled } from "../RateNegotiation/isShiftRateNegotiationEnabled";
import { useShiftRateNegotiationConditions } from "../RateNegotiation/useShiftRateNegotiationConditions";
import { type ModalShiftData } from "../types";

/**
 * A shift is deemed bookable, i.e. we allow the worker to book it but showing the Book button if
 * 1. The bookability status is ALLOWED
 * 2. There isn't a windowing restriction on the shift
 * 3. There are no blocking requirements
 *
 * If the shift is blocked for booking because the worker needs to provide some data for background checks,
 * then we still allow them to book the shift, but collect the data before we confirm it.
 * This is done in `performChecksBeforeBooking` in `src/appV2/redesign/Shift/Booking/useBookShift.ts`
 */
function getIsBookable(params: {
  bookabilityStatus: BookabilityStatusItem | undefined;
  shift: ModalShiftData | undefined;
  blockingRequirementsContext: BlockingRequirementsContext | undefined;
  backgroundCheckContext: BackgroundCheckContext;
}): boolean {
  const { bookabilityStatus, shift, blockingRequirementsContext, backgroundCheckContext } = params;
  return (
    (bookabilityStatus?.attributes.bookability.decision === BookabilityDecision.ALLOWED ||
      backgroundCheckContext.isBlockedOnlyDueToMissingData) &&
    isNil(shift?.attributes.windowRestriction) &&
    !blockingRequirementsContext?.hasBlockingRequirements
  );
}

/**
 * Returns `true` if the worker needs to complete something before even starting to mark interest in the shift.
 * For example: They need to complete some documents before marking interest.
 *
 * This function evaluates to true only if
 * 1. The instant book is disabled
 * 2. The shift is not bookable
 * 3. There are more unmet criteria apart from `SHIFT_INSTANT_BOOK_DISABLED` and `WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK`
 *    a. If background check data is missing, there should be more than 2 unmet criteria
 *    b. If background check data is not missing, there should be more than 1 unmet criteria
 */
function getNeedsCriteriaBeforeMarkingInterest(params: {
  bookabilityStatus: BookabilityStatusItem | undefined;
  isInstantBookDisabled: boolean;
  isBookable: boolean;
  isMissingDataForBackgroundCheck: boolean;
}): boolean {
  const { bookabilityStatus, isInstantBookDisabled, isBookable, isMissingDataForBackgroundCheck } =
    params;
  return (
    isInstantBookDisabled &&
    !isBookable &&
    bookabilityStatus?.attributes.bookability.decision === BookabilityDecision.BLOCKED &&
    ((!isMissingDataForBackgroundCheck &&
      bookabilityStatus?.attributes.bookability.unmetCriteria.length > 1) ||
      (isMissingDataForBackgroundCheck &&
        bookabilityStatus?.attributes.bookability.unmetCriteria.length > 2))
  );
}

export function useFooterData() {
  const {
    navigateToModal,
    shiftId: shiftIdFromParams,
    shift,
    bookabilityStatus,
    hasMarkedInterest,
    blockingRequirementsContext,
  } = useShiftModalsDataContext();

  const { workplace, offer } = shift ?? {};

  const shiftId = shift?.id ?? shiftIdFromParams;
  const backgroundCheckContext = getBackgroundCheckContext(bookabilityStatus);
  const missingBackgroundCheckDataTypes = backgroundCheckContext.missingDataTypes;

  const isBookable = getIsBookable({
    bookabilityStatus,
    shift,
    blockingRequirementsContext,
    backgroundCheckContext,
  });

  const isInstantBookDisabled = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED,
  ]);
  const isMissingDataForBackgroundCheck = backgroundCheckContext.isMissingData;

  const needsCriteriaBeforeMarkingInterest = getNeedsCriteriaBeforeMarkingInterest({
    bookabilityStatus,
    isInstantBookDisabled,
    isBookable,
    isMissingDataForBackgroundCheck,
  });

  const isTestAccount = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_DISABLED_TEST_ACCOUNT,
  ]);

  const isShiftAlreadyBooked = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.SHIFT_ALREADY_GRABBED,
  ]);

  const rateNegotiationConditions = useShiftRateNegotiationConditions();

  const hasWorkerRejectedRateNegotiation = Boolean(
    shift?.rateNegotiation?.status === ShiftRateNegotiationStatus.REJECTED &&
      shift?.rateNegotiation?.lastOfferBy === ShiftRateNegotiationRole.WORKER
  );

  const isRateNegotiationEnabled =
    isDefined(shift) &&
    isDefined(workplace) &&
    isShiftRateNegotiationEnabled(
      {
        shiftStart: shift.attributes.start,
        workplaceShiftRateNegotiability:
          workplace.attributes.rateNegotiation ?? WorkplaceShiftRateNegotiability.OFF,
        workplaceId: workplace.id,
        workplaceState: workplace.attributes.location.state,
        workplaceMsa: workplace.attributes.location.msa,
        isShiftChargeUpdatedManually: shift.attributes.isChargeUpdatedManually,
      },
      rateNegotiationConditions
    ) &&
    !hasWorkerRejectedRateNegotiation;

  const showMarkInterestButton =
    !needsCriteriaBeforeMarkingInterest && isInstantBookDisabled && !hasMarkedInterest;

  const shouldHideBookCta =
    (isBookable && (isInstantBookDisabled || isTestAccount)) || showMarkInterestButton;

  return {
    isBookable,
    missingBackgroundCheckDataTypes,
    isInstantBookDisabled,
    isTestAccount,
    isRateNegotiationEnabled,
    isShiftAlreadyBooked,
    shouldHideBookCta,
    shift,
    shiftId,
    workplace,
    offer,
    navigateToModal,
    showMarkInterestButton,
  };
}
